import { useTranslate } from '@pankod/refine-core'

import {
  Divider,
  Drawer,
  DrawerProps,
  Form,
  FormProps,
  Grid,
  Input,
  message,
  Radio,
  SaveButton,
  Typography,
  useModal
} from '@pankod/refine-antd'
import { QueryObserverResult } from '@tanstack/react-query'
import { AdviceModal } from 'components/AdviceModal/AdviceModal'
import { PendingWarehouseNoteModal } from 'components/orders/PendingWarehouseNoteModal'
import { Islands, Order, ShippingMethods } from 'interfaces'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { currentUserMetadata } from 'utility/authLocal'
import { ADMIN_ROLE_ID, ADMITTED_STATUS, CANCELLED_STATUS, NO_STOCK_STATUS, ORDER_STATUS_ID_PENDING_PAYMENT, ORDER_STATUS_ID_REQUESTED, PENALIZATION_STATUS, PENALIZATION_STATUS_TEXT, SENT_BUDGE_STATUS_ID, SHIPPING_METHOD_REDUCED_ID, SUPPLEMENT_PREDEFINED_OPTIONS } from 'utility/constants'
import { checkIfInvoiceIsEditable, checkIssueDeadline, getVariableValue, updateCalculatedTotalOrder } from 'utility/supabaseApi'
import { supabaseClient } from 'utility/supabaseClient'
import { getWarehouseVolumetricBlock, warehousePreparationAdviceKey } from 'utility/warehousePreparationGate'
import { NotVolSuplementsForm } from './notVolSuplementForm'
import { VolSuplementsForm } from './volSuplementForm'

type SupplementActionsProps = {
  drawerProps: DrawerProps
  formProps: FormProps
  closeDrawer: Function
  tableQueryResult: QueryObserverResult
  orderId: number
  edit?: boolean
  shippingMethod: ShippingMethods
  island: Islands,
  orderQuery: QueryObserverResult,
  packageNumber: number
  order: Order
  isEstimatedCost?: boolean
  onAutomaticallyAdmittedVolumetric?: (supplement: any) => void
  canAutomaticallyPrepareVolumetric?: boolean
  volumetricOnly?: boolean
  warehouseReviewChecklistGuard?: () => Promise<boolean>
}

export const SupplementActions: React.FC<SupplementActionsProps> = ({
  drawerProps,
  formProps,
  closeDrawer,
  tableQueryResult,
  edit = false,
  orderId,
  shippingMethod,
  island,
  orderQuery,
  packageNumber,
  order,
  isEstimatedCost,
  onAutomaticallyAdmittedVolumetric,
  canAutomaticallyPrepareVolumetric = false,
  volumetricOnly = false,
  warehouseReviewChecklistGuard
}) => {
  const t = useTranslate()
  const breakpoint = Grid.useBreakpoint()

  const { modalProps, show, close } = useModal()
  const [adviceText, setAdviceText] = useState('')

  const [loading, setLoading] = useState(false)
  // const [shippingPrice, setShippingPrice] = useState(0)
  const [volumetric, setVolumetric] = useState<boolean>(volumetricOnly || isEstimatedCost || false)
  const [maxKgDefault, setMaxKgDefault] = useState(0)
  const [statusId, setStatusId] = useState(ORDER_STATUS_ID_PENDING_PAYMENT)
  const [reload, setReload] = useState(true)
  const [radioSelectedValue, setRadioSelectedValue] = useState(false)

  const { modalProps: modalPropsPendingWarehouseNote, show: showPendingWarehouseNote, close: closePendingWarehouseNote } = useModal()

  let volSuplements: any
  let notVolSuplements: any

  const handleVolSuplements = (data: any) => {
    volSuplements = [...data]
  }

  const handleNotVolSuplements = (data: any) => {
    notVolSuplements = data
  }

  useEffect(() => {
    if (reload === false) {
      setReload(true)
    }
  }, [reload])

  const formData = formProps.initialValues

  getVariableValue('MAX_KG_DEFAULT_SHIPPING_PRICE', setMaxKgDefault)

  const extraCost = shippingMethod.shipment_type === 'plane'
    ? island?.plane_extra_cost
    : island?.boat_extra_cost

  if (edit) {
    useEffect(() => {
      setVolumetric(formData?.volumetric)
      setStatusId(formData?.status_id || ORDER_STATUS_ID_PENDING_PAYMENT)
    }, [formData])
  }

  const isAdmin = currentUserMetadata()?.role_id === ADMIN_ROLE_ID

  const shouldAutomaticallyPrepareVolumetric = (state: any, previousStatus?: number) => {
    return !!(
      onAutomaticallyAdmittedVolumetric &&
      !isEstimatedCost &&
      canAutomaticallyPrepareVolumetric &&
      state.volumetric &&
      state.status_id === ADMITTED_STATUS &&
      previousStatus !== ADMITTED_STATUS
    )
  }

  const waitForAutomaticVolumetricEmail = async (supplementId: number) => {
    const retries = 20
    const retryDelayMs = 1000

    for (let attempt = 0; attempt < retries; attempt++) {
      const { data, error } = await supabaseClient
        .from('supplements')
        .select('email_sent_at')
        .eq('id', supplementId)
        .single()

      if (!error && data?.email_sent_at !== null) return true

      await new Promise(resolve => window.setTimeout(resolve, retryDelayMs))
    }

    return false
  }

  const supplementOptions = SUPPLEMENT_PREDEFINED_OPTIONS.map((option) => ({ value: option, label: t(`supplement.descriptions.${option}`) }))

  const VolumetricForm = () => {
    return (
      <>
        {<div>
          <VolSuplementsForm
            edit={edit}
            isAdmin={isAdmin}
            formData={formData}
            packageNumber={packageNumber}
            orderId={orderId}
            handleVolSuplements={handleVolSuplements}
            shippingMethod={shippingMethod}
            maxKgDefault={maxKgDefault}
            extraCost={extraCost}
            order={order} />
        </div>}
      </>
    )
  }

  const NoVolumetricForm = () => {
    return (<NotVolSuplementsForm
      edit={edit}
      supplementData={formData}
      supplementOptions={supplementOptions}
      shippingMethod={shippingMethod}
      handleNotVolSuplements={handleNotVolSuplements}
    />)
  }

  const markEditedOrder = async (editable: boolean) => {
    if (!editable && !isEstimatedCost) {
      await supabaseClient.from('orders')
        .update({
          edited: true
        })
        .eq('id', orderId)
    }
  }

  const saveSup = async () => {
    const preparationBlock = volumetricOnly ? getWarehouseVolumetricBlock(order) : null
    if (preparationBlock) {
      message.error(t(warehousePreparationAdviceKey(order, 'supplement')))
      return
    }
    if (volumetricOnly && warehouseReviewChecklistGuard && !(await warehouseReviewChecklistGuard())) return
    setLoading(true)
    let automaticallyAdmittedVolumetric: any
    const pendingVolumetricEmails: number[] = []
    // Check if the invoice is editable before making changes
    const { editable } = await checkIfInvoiceIsEditable(order.id)
    const resource = isEstimatedCost ? 'estimated_costs' : 'supplements'
    if (notVolSuplements !== undefined) {
      if (notVolSuplements.id) {
        await updateSupplement(notVolSuplements, resource)
      } else {
        await insertSupplement(notVolSuplements, resource)
      }
      notVolSuplements = undefined
      markEditedOrder(editable)
    }

    if (volSuplements !== undefined) {
      for (const sup of volSuplements) {
        const previousStatus = sup.id ? formData?.status_id : undefined
        let savedSupplement
        if (sup.id) {
          savedSupplement = await updateSupplement(sup, resource)
        } else {
          savedSupplement = await insertSupplement(sup, resource)
        }
        if (shouldAutomaticallyPrepareVolumetric(savedSupplement, previousStatus)) {
          automaticallyAdmittedVolumetric = savedSupplement
        }
        if (!sup.id && !isEstimatedCost && savedSupplement?.volumetric && savedSupplement?.status_id === ORDER_STATUS_ID_PENDING_PAYMENT && Number.isInteger(savedSupplement.id)) {
          pendingVolumetricEmails.push(savedSupplement.id)
        }
      }
      markEditedOrder(editable)
    }
    if (automaticallyAdmittedVolumetric) {
      setLoading(false)
      closeDrawer()
      onAutomaticallyAdmittedVolumetric?.(automaticallyAdmittedVolumetric)
      return
    }

    if (pendingVolumetricEmails.length > 0) {
      await Promise.all(pendingVolumetricEmails.map(waitForAutomaticVolumetricEmail))
    }

    setLoading(false)
    closeDrawer()
    window.location.reload()
  }

  const calculateRefundStore = async (supplement: number, iva?: number) => {
    const { data, error } = await supabaseClient.from('orders').select('shipping_method_id').eq('id', orderId).single()

    const { data: dataEconomyShipment, error: errorShipment } = await supabaseClient.from('shipment_methods').select('id').eq('shipMode', true).eq('is_in_home', true).single()

    if (error || errorShipment) {
      return supplement
    }

    // eslint-disable-next-line camelcase
    if (data.shipping_method_id !== dataEconomyShipment.id && data.shipping_method_id !== SHIPPING_METHOD_REDUCED_ID) {
      const percent = (iva !== undefined && iva >= 0) ? Number(`1.${iva}`) : 1.21
      return (supplement / percent).toFixed(2)
    }
    return supplement
  }

  const insertSupplement = async (dataToInsert: any, resource: string) => {
    // eslint-disable-next-line camelcase
    const { volumetric, weigth, length, heigth, depth, volume, billable_volume, weigth_excess, description, reason, volumetricInfo } = dataToInsert

    let { supplement } = dataToInsert

    supplement = (description === 'refundStore') ? await calculateRefundStore(supplement, (order?.marketplace_id?.id > 0 && order?.marketplace_id?.iva_percent >= 0) ? order?.marketplace_id?.iva_percent : undefined) : supplement

    let status = supplement <= 0 || description === PENALIZATION_STATUS_TEXT ? ADMITTED_STATUS : statusId
    if (isEstimatedCost) {
      status = SENT_BUDGE_STATUS_ID
    }

    if (volumetric && volumetricInfo) {
      await supabaseClient.from('orders').update({
        package_number: Number(volumetricInfo.length) || 1,
        updated_by: supabaseClient.auth.user()?.email
      }).eq('id', orderId)
    }

    const state = {
      order_id: orderId,
      volumetric: volumetric,
      weigth: volumetric ? weigth : null,
      length: volumetric ? length : null,
      heigth: volumetric ? heigth : null,
      depth: volumetric ? depth : null,
      volume: volumetric ? volume : null,
      // eslint-disable-next-line camelcase
      billable_volume: volumetric ? billable_volume : null,
      // eslint-disable-next-line camelcase
      weigth_excess: volumetric ? weigth_excess : null,
      supplement: supplement,
      description: !volumetric ? description : null,
      reason: (description === 'other' || description === 'refund') ? reason : null,
      status_id: status,
      volumetricInfo: volumetric ? volumetricInfo : null,
      updated_by: supabaseClient.auth.user()?.email
    }

    const { data: insertedSupplement } = await supabaseClient
      .from(resource)
      .insert(state)
      .select('id')
      .single()

    const arrayPromise = []
    if (!isEstimatedCost) arrayPromise.push(updateCalculatedTotalOrder(orderId))
    // if (!volumetric && (description?.toLowerCase() === 'refundtransport' || description?.toLowerCase() === 'refundByCancelation' || description?.toLowerCase() === 'refundstore' || description?.toLowerCase() === 'refund')) {
    //   const refund = {
    //     description: (description === 'refund') ? reason : description,
    //     amount: Math.abs(supplement),
    //     status_id: 0,
    //     order_id: orderId,
    //     type: 'client',
    //     updated_by: supabaseClient.auth.user()?.email
    //   }

    //   arrayPromise.push(supabaseClient.from('refunds').insert(refund))
    // }
    if (isEstimatedCost) {
      arrayPromise.push(supabaseClient.from('orders')
        .update({
          estimated_costs_status: SENT_BUDGE_STATUS_ID,
          updated_by: supabaseClient.auth.user()?.email
        })
        .eq('id', orderId))
    }

    if (volumetric) {
      checkIssueDeadline(orderId)
    }

    await Promise.all(arrayPromise)

    tableQueryResult.refetch()
    orderQuery.refetch()
    return { ...state, id: insertedSupplement?.id }
  }

  const updateSupplement = async (dataToUpdate: any, resource: string) => {
    // eslint-disable-next-line camelcase
    const { volumetric, weigth, length, heigth, depth, volume, billable_volume, weigth_excess, description, reason, id, volumetricInfo } = dataToUpdate

    let { supplement } = dataToUpdate

    supplement = (description === 'refundStore') ? await calculateRefundStore(supplement) : supplement

    let status = supplement <= 0 || description === PENALIZATION_STATUS_TEXT ? ADMITTED_STATUS : statusId
    if (isEstimatedCost) {
      status = SENT_BUDGE_STATUS_ID
    }

    if (volumetric && volumetricInfo) {
      await supabaseClient.from('orders').update({
        package_number: Number(volumetricInfo.length) || 1,
        updated_by: supabaseClient.auth.user()?.email
      }).eq('id', orderId)
    }

    const state = {
      order_id: orderId,
      volumetric: volumetric,
      weigth: volumetric ? weigth : null,
      length: volumetric ? length : null,
      heigth: volumetric ? heigth : null,
      depth: volumetric ? depth : null,
      volume: volumetric ? volume : null,
      // eslint-disable-next-line camelcase
      billable_volume: volumetric ? billable_volume : null,
      // eslint-disable-next-line camelcase
      weigth_excess: volumetric ? weigth_excess : null,
      supplement: supplement,
      description: !volumetric ? description : null,
      reason: description === 'other' ? reason : null,
      status_id: status,
      volumetricInfo: volumetric ? volumetricInfo : null,
      updated_at: moment()
    }

    await supabaseClient.from(resource).update(state).eq('id', id)

    const arrayPromise = []
    if (!isEstimatedCost) arrayPromise.push(updateCalculatedTotalOrder(orderId))

    // if (!volumetric && (description?.toLowerCase() === 'refundtransport' || description?.toLowerCase() === 'refund' || description?.toLowerCase() === 'refundByCancelation' || description?.toLowerCase() === 'refundstore')) {
    //   const ref = await supabaseClient.from<definitions['refunds']>('refunds').select('*').eq('order_id', orderId).like('description', description).single()

    //   if (ref?.data) {
    //     arrayPromise.push(supabaseClient.from('refunds').update({
    //       amount: Math.abs(supplement),
    //       updated_by: supabaseClient.auth.user()?.email
    //     }).eq('id', ref.data.id))
    //   } else {
    //     arrayPromise.push(supabaseClient.from('refunds').insert({
    //       description: description,
    //       amount: Math.abs(supplement),
    //       status_id: 0,
    //       order_id: orderId,
    //       type: 'client',
    //       updated_by: supabaseClient.auth.user()?.email
    //     }))
    //   }
    // }

    await Promise.all(arrayPromise)

    tableQueryResult.refetch()
    orderQuery.refetch()
    return { ...state, id }
  }

  return (
    <>
      <Drawer
        {...drawerProps}
        width={breakpoint.sm ? '600px' : '100%'}
        bodyStyle={{ padding: 0 }}
        zIndex={500}
        closable={!loading}
        maskClosable={!loading}
      >
        {Number.isInteger(formData?.order_id) && (
          <div style={{ margin: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography style={{ fontSize: 20, fontWeight: 600, marginBottom: 20 }}>{edit ? t('order.actions.editSupplement') : t('order.actions.addSupplement')}</Typography>
            </div>
            <Form
              style={{ margin: '1rem' }}
              layout="vertical"
              // onFinish={edit ? updateSupplement : insertSupplement}
            >
              <Form.Item
                hidden={true}
                name="order_id"
              >
                <Input />
              </Form.Item>
              {(!isEstimatedCost && !volumetricOnly) && <Form.Item
                label={t('supplement.isVolumetric')}
                name="volumetric"
                hidden={edit}
              >
                <Radio.Group
                  onChange={(event) => {
                    const currentYear = new Date().getFullYear()
                    const orderYear = new Date(order?.created_at!).getFullYear()

                    if (order?.status_id === ORDER_STATUS_ID_REQUESTED && event.target.value) {
                      setAdviceText(t('supplement.advise.requestedPrepare'))
                      setRadioSelectedValue(event.target.value)
                      show()
                    } else if (!edit && event.target.value && typeof order?.status_id === 'number' && [ORDER_STATUS_ID_PENDING_PAYMENT, CANCELLED_STATUS, NO_STOCK_STATUS, PENALIZATION_STATUS].includes(order?.status_id)) {
                      setAdviceText(t('supplement.advise.badStatus'))
                      setRadioSelectedValue(event.target.value)
                      show()
                    } else if (!edit && event.target.value && order.supplements.find((s) => s.volumetric)) {
                      setAdviceText(t('supplement.advise.duplicatedSup'))
                      setRadioSelectedValue(event.target.value)
                      show()
                    } else if (orderYear < currentYear) {
                      setAdviceText(t('supplement.advise.lastYear', { operation: t('supplement.advise.lastYearSup') }))
                      setRadioSelectedValue(event.target.value)
                      show()
                    } else if (order.cancelation_date !== null) {
                      setAdviceText(t('supplement.advise.cancellationRequestSup'))
                      setRadioSelectedValue(event.target.value)
                      show()
                    } else if (order?.warehouseNote?.trim() && event.target.value) {
                      setVolumetric(event.target.value)
                      showPendingWarehouseNote()
                    } else {
                      setVolumetric(event.target.value)
                    }
                  }}
                >
                  <Radio value={true}>{t('status.yes')}</Radio>
                  <Radio value={false}>{t('status.no')}</Radio>
                </Radio.Group>
              </Form.Item>}
              {volumetric && <VolumetricForm />}
              {!volumetric && <NoVolumetricForm />}
              <Divider />
              <SaveButton
                loading={loading}
                style={{ float: 'right', marginBottom: '5rem' }}
                onClick={() => saveSup()}>
                  {t('buttons.save')}
              </SaveButton>
            </Form>
          </div>
        )}

      </Drawer>
      <AdviceModal
        modalProps={modalProps}
        close={close}
        show={show}
        infoText={adviceText}
        okFunction={() => {
          setVolumetric(radioSelectedValue)
          order?.warehouseNote?.trim() && radioSelectedValue && showPendingWarehouseNote()
        }}
      />
      <PendingWarehouseNoteModal
        modalProps={modalPropsPendingWarehouseNote}
        close={closePendingWarehouseNote}
        warehouseNote={order?.warehouseNote || ''}
      />
    </>
  )
}
